import TrackingEvent, { TrackingProperty } from 'constants/trackingEvents';
import { useCurrentUser } from 'hooks/new/useCurrentUser';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import {
  goToAllSpaces,
  goToConversations,
  goToEvents,
  goToHome,
  goToSearch,
} from 'utils/routerHelpers';

import React, { useMemo } from 'react';

import { CalendarBlank, Chats, House, MagnifyingGlass, Users } from '@thinkific/chalk/react-icons';
import { MobileTabNav } from '@thinkific/chalk/recipes/MobileTabNav';

import { useCommunityContext } from 'components/CommunityContext';
import { useChatContext } from 'components/new/chat/providers/ChatProvider/ChatProvider';

import * as css from './MobileChin.css';

interface MobileChinProps {
  community: {
    name: string;
    id: string;
  };
}

export const MobileChin = ({ community }: MobileChinProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const { trackEvent } = useCommunityContext();
  const { isDirectMessagingEnabled, hasUnreadMessages } = useChatContext();
  const { interactions } = useCurrentUser(community.id);

  const hasUnreadSpaces = useMemo(() => {
    return interactions.some((interaction) => interaction.hasUnreadPosts);
  }, [interactions]);
  const tabs = [
    {
      id: 'home',
      dataUserPilotId: 'home-mobile-tab',
      label: t('pages-home_title', 'Home'),
      icon: House,
      href: `/communities/${community.id}/`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_HOME, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToHome(router, community.id);
      },
    },
    {
      id: 'spaces',
      dataUserPilotId: 'spaces-mobile-tab',
      label: t('pages-spaces_title', 'Spaces'),
      icon: Users,
      href: `/communities/${community.id}/spaces`,
      indicator: hasUnreadSpaces ? <div className={css.indicator} /> : null,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_SPACES, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToAllSpaces(router, community.id);
      },
    },
    ...(isDirectMessagingEnabled
      ? [
          {
            id: 'conversations',
            dataUserPilotId: 'conversations-mobile-tab',
            label: t('pages-conversations_title', 'DMs'),
            icon: Chats,
            href: `/communities/${community.id}/conversation`,
            indicator: hasUnreadMessages ? <div className={css.indicator} /> : null,
            onClick: (e) => {
              e.preventDefault();
              goToConversations(router, community.id);
            },
          },
        ]
      : []),
    {
      id: 'events',
      dataUserPilotId: 'events-mobile-tab',
      label: t('pages-events_title', 'Events'),
      icon: CalendarBlank,
      href: `/communities/${community.id}/events`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.CLICK_SEE_ALL_EVENTS, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToEvents(router, community.id);
      },
    },
    {
      id: 'search',
      dataUserPilotId: 'search-mobile-tab',
      label: t('pages-search_title', 'Search'),
      icon: MagnifyingGlass,
      href: `/communities/${community.id}/search`,
      onClick: (e) => {
        e.preventDefault();
        trackEvent(TrackingEvent.GO_TO_SEARCH, {
          [TrackingProperty.COMPONENT]: 'MobileNav',
        });
        goToSearch(router, community.id);
      },
    },
  ];

  return (
    <div className={css.container} data-qa="mobile-chin">
      <MobileTabNav
        currentTabId={tabs.find((tab) => router.route.includes(tab.id))?.id || 'home'}
        tabs={tabs}
      />
    </div>
  );
};

export default MobileChin;
